/**
 * Consent-Aware Analytics Initialization
 * Handles initialization of analytics scripts only after user consent
 * Supports GA4, PostHog, and other tracking services
 */

import { hasConsent, onConsentChange, getConsent } from './cookieConsent';

// Track initialization state
let ga4Initialized = false;
let posthogInitialized = false;

// Configuration
const GA4_MEASUREMENT_ID = 'G-13JBQZG01T';
const POSTHOG_API_KEY = 'phc_yJW1VjHGGwmCbbrtczfqqNxgBDbhlhOWcdzcIJEOTFE';
const POSTHOG_API_HOST = 'https://us.i.posthog.com';

/**
 * Initialize Google Analytics 4 with consent mode
 */
export function initGA4() {
  if (ga4Initialized || typeof window === 'undefined') return;

  try {
    // Create gtag function
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;

    // Set default consent state (denied until user consents)
    gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      wait_for_update: 500,
    });

    // Initialize with timestamp
    gtag('js', new Date());

    // Configure GA4
    gtag('config', GA4_MEASUREMENT_ID, {
      send_page_view: true,
      anonymize_ip: true,
    });

    // Load the GA4 script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    ga4Initialized = true;
    console.log('[Analytics] GA4 initialized with consent mode');

    // Update consent based on current preferences
    updateGA4Consent();
  } catch (error) {
    console.error('[Analytics] Error initializing GA4:', error);
  }
}

/**
 * Update GA4 consent based on current preferences
 */
export function updateGA4Consent() {
  if (!ga4Initialized || typeof window === 'undefined' || !window.gtag) return;

  const consent = getConsent();
  if (!consent) return;

  try {
    window.gtag('consent', 'update', {
      analytics_storage: consent.analytics ? 'granted' : 'denied',
      ad_storage: consent.marketing ? 'granted' : 'denied',
      ad_user_data: consent.marketing ? 'granted' : 'denied',
      ad_personalization: consent.marketing ? 'granted' : 'denied',
    });

    console.log('[Analytics] GA4 consent updated:', {
      analytics: consent.analytics,
      marketing: consent.marketing,
    });
  } catch (error) {
    console.error('[Analytics] Error updating GA4 consent:', error);
  }
}

/**
 * Initialize PostHog with consent check
 */
export function initPostHog() {
  if (posthogInitialized || typeof window === 'undefined') return;

  // Only initialize if analytics consent is granted
  if (!hasConsent('analytics')) {
    console.log('[Analytics] PostHog initialization deferred - awaiting consent');
    return;
  }

  try {
    // Dynamically import PostHog
    import('posthog-js')
      .then((module) => {
        const posthog = module.default;

        posthog.init(POSTHOG_API_KEY, {
          api_host: POSTHOG_API_HOST,
          person_profiles: 'identified_only',
          persistence: 'localStorage',
          autocapture: hasConsent('analytics'),
          capture_pageview: hasConsent('analytics'),
          disable_session_recording: !hasConsent('analytics'),
        });

        window.posthog = posthog;
        posthogInitialized = true;

        console.log('[Analytics] PostHog initialized');
      })
      .catch((error) => {
        console.error('[Analytics] Error loading PostHog:', error);
      });
  } catch (error) {
    console.error('[Analytics] Error initializing PostHog:', error);
  }
}

/**
 * Opt out of PostHog tracking
 */
export function optOutPostHog() {
  if (typeof window !== 'undefined' && window.posthog) {
    try {
      window.posthog.opt_out_capturing();
      console.log('[Analytics] PostHog opted out');
    } catch (error) {
      console.error('[Analytics] Error opting out PostHog:', error);
    }
  }
}

/**
 * Opt in to PostHog tracking
 */
export function optInPostHog() {
  if (typeof window !== 'undefined' && window.posthog) {
    try {
      window.posthog.opt_in_capturing();
      console.log('[Analytics] PostHog opted in');
    } catch (error) {
      console.error('[Analytics] Error opting in PostHog:', error);
    }
  }
}

/**
 * Initialize all analytics based on current consent
 */
export function initializeAnalytics() {
  if (typeof window === 'undefined') return;

  // Always initialize GA4 (with consent mode)
  initGA4();

  // Check current consent
  const consent = getConsent();

  if (consent) {
    // Update GA4 consent state
    updateGA4Consent();

    // Initialize PostHog if analytics consent is granted
    if (consent.analytics) {
      initPostHog();
    }
  }

  // Listen for consent changes
  onConsentChange((updatedConsent) => {
    console.log('[Analytics] Consent changed:', updatedConsent);

    // Update GA4 consent
    updateGA4Consent();

    // Handle PostHog based on analytics consent
    if (updatedConsent.analytics) {
      if (!posthogInitialized) {
        initPostHog();
      } else {
        optInPostHog();
      }
    } else {
      optOutPostHog();
    }

    // Reload page if marketing consent changed (for third-party pixels)
    // This ensures pixels like Facebook Pixel are properly loaded/unloaded
    if (consent && consent.marketing !== updatedConsent.marketing) {
      console.log('[Analytics] Marketing consent changed - page may need reload for full effect');
    }
  });
}

/**
 * Check if analytics tracking is allowed
 */
export function canTrack() {
  return hasConsent('analytics');
}

/**
 * Check if marketing tracking is allowed
 */
export function canTrackMarketing() {
  return hasConsent('marketing');
}

/**
 * Consent-aware wrapper for analytics events
 * Use this to ensure events are only sent with proper consent
 */
export function trackWithConsent(category, trackingFunction, ...args) {
  if (!hasConsent(category)) {
    console.log(`[Analytics] Tracking blocked - no consent for ${category}`);
    return;
  }

  try {
    trackingFunction(...args);
  } catch (error) {
    console.error('[Analytics] Error tracking event:', error);
  }
}
